import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardText, Buildings, Storefront, Users, Target, CurrencyCircleDollar,
  DeviceMobile, UsersThree, ChartPieSlice, ArrowRight, ArrowLeft, ArrowsClockwise,
  Lightning, ShieldWarning, TrendUp, TrendDown, CaretDown, CaretRight,
  ClockCounterClockwise, Trash, Eye,
} from "@phosphor-icons/react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip,
} from "recharts";

const EMPTY = {
  company_name: "", cnpj: "", city_uf: "",
  operation_type: "", regions: [],
  current_categories: [], top_selling: "", best_margin: "", pain_products: "",
  expansion_lines: [], research_goals: [],
  current_clients: [], target_clients: [],
  sales_channels_active: [],
  client_base_organized: "", has_segment_list: "", has_recurring_routine: "",
  desired_ticket: "",
  competitors_to_watch: "", dubom_as_reference: "",
  compare_aspects: [], channels_to_monitor: [],
  has_price_table: "", pricing_strategy: "", has_margin_target: "",
  price_comparison_goal: "", client_price_access: "",
  has_website: "", whatsapp_business: "", instagram_active: "",
  automation_needs: [], expected_gain: "",
  roles_to_hire: [], researcher_focus: [], sales_focus: [],
  adaptation_request: "",
};

// ===== Reusable form pieces =====
const Field = ({ label, children, hint }) => (
  <div className="space-y-1">
    <label className="label-eyebrow block">{label}</label>
    {children}
    {hint && <p className="text-xs" style={{ color: "var(--text-muted)" }}>{hint}</p>}
  </div>
);

const Chips = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((opt) => {
      const active = value.includes(opt);
      return (
        <button
          type="button"
          key={opt}
          onClick={() =>
            onChange(active ? value.filter((v) => v !== opt) : [...value, opt])
          }
          className="text-xs px-3 py-1.5 transition-colors"
          style={{
            background: active ? "var(--gold)" : "var(--bg-surface)",
            color: active ? "#fff" : "var(--text-secondary)",
            border: `1px solid ${active ? "var(--gold)" : "var(--border-default)"}`,
            borderRadius: 2,
          }}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const Radios = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((opt) => {
      const active = value === opt;
      return (
        <button
          type="button"
          key={opt}
          onClick={() => onChange(active ? "" : opt)}
          className="text-xs px-3 py-1.5 transition-colors"
          style={{
            background: active ? "var(--gold)" : "var(--bg-surface)",
            color: active ? "#fff" : "var(--text-secondary)",
            border: `1px solid ${active ? "var(--gold)" : "var(--border-default)"}`,
            borderRadius: 2,
          }}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const Section = ({ index, title, icon: Icon, open, onToggle, children }) => (
  <section className="surface overflow-hidden" data-testid={`section-${index}`}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-5 py-4 text-left border-b transition-colors"
      style={{ borderColor: open ? "var(--border-default)" : "transparent", background: open ? "var(--bg-surface-hover)" : "transparent" }}
    >
      <span className="text-xs font-semibold px-2 py-1" style={{ color: "var(--gold)", background: "rgba(184,148,31,0.10)", border: "1px solid rgba(184,148,31,0.25)" }}>
        {String(index).padStart(2, "0")}
      </span>
      <Icon size={18} weight="duotone" style={{ color: "var(--gold)" }} />
      <span className="font-medium flex-1" style={{ color: "var(--text-primary)" }}>{title}</span>
      {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
    </button>
    {open && <div className="p-5 space-y-5">{children}</div>}
  </section>
);

// ===== Result / Diagnosis view =====
const Diagnosis = ({ b, onReset, evolution }) => {
  const scoreData = useMemo(() => {
    const ms = b.maturity_scores || {};
    const prev = evolution?.previous?.maturity_scores || {};
    const hasPrev = !!evolution?.previous;
    return Object.entries(ms)
      .filter(([k]) => k !== "overall")
      .map(([k, v]) => ({ dim: k, atual: v, ...(hasPrev ? { anterior: prev[k] || 0 } : {}) }));
  }, [b, evolution]);

  const overall = b.maturity_scores?.overall ?? 0;
  const hasPrev = !!evolution?.previous;
  const prevOverall = evolution?.previous?.maturity_scores?.overall;
  const overallDelta = hasPrev ? overall - prevOverall : null;

  return (
    <div className="space-y-6" data-testid="diagnosis-view">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Diagnóstico Estratégico</div>
          <h1 className="h-display" style={{ color: "var(--text-primary)" }}>{b.company_name}</h1>
          <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
            CNPJ {b.cnpj} · {b.city_uf} · {b.operation_type}
          </p>
        </div>
        <button onClick={onReset} className="btn-ghost px-4 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2" data-testid="diagnosis-edit">
          <ArrowsClockwise size={14} /> Novo briefing
        </button>
      </header>

      {/* Summary card */}
      <section className="surface p-6" data-testid="diagnosis-summary">
        <div className="flex items-center gap-2 mb-3">
          <Lightning size={18} weight="duotone" style={{ color: "var(--gold)" }} />
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Diagnóstico preliminar</h2>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{b.summary}</p>
      </section>

      {/* Chart + overall score */}
      <section className="surface p-4 md:p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
        <div className="lg:col-span-2">
          <div className="label-eyebrow mb-3 flex items-center gap-2">
            <span>Maturidade por dimensão</span>
            {hasPrev && (
              <span className="text-[10px] px-2 py-0.5 normal-case tracking-normal" style={{ background: "rgba(184,148,31,0.10)", color: "var(--gold)", border: "1px solid rgba(184,148,31,0.25)" }}>
                evolução vs briefing anterior
              </span>
            )}
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <RadarChart data={scoreData} outerRadius="75%">
                <PolarGrid stroke="var(--border-default)" />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "var(--text-secondary)", fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "var(--text-muted)", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)", borderRadius: 2, fontSize: 12 }} />
                {hasPrev && (
                  <Radar name="Anterior" dataKey="anterior" stroke="var(--text-muted)" fill="var(--text-muted)" fillOpacity={0.10} strokeDasharray="4 4" />
                )}
                <Radar name="Atual" dataKey="atual" stroke="var(--gold)" fill="var(--gold)" fillOpacity={0.28} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="text-center lg:text-left">
          <div className="label-eyebrow mb-2">Maturidade global</div>
          <div className="text-6xl font-light tracking-tight" style={{ color: "var(--gold)" }}>
            {overall}<span className="text-2xl text-[var(--text-muted)]">/100</span>
          </div>
          {hasPrev && overallDelta != null && (
            <div className="text-xs mt-1" style={{ color: overallDelta >= 0 ? "var(--success)" : "var(--danger)" }}>
              {overallDelta > 0 ? "▲" : overallDelta < 0 ? "▼" : "—"} {overallDelta > 0 ? "+" : ""}{overallDelta} pts vs. anterior
            </div>
          )}
          <div className="space-y-1 mt-4 text-xs">
            {scoreData.map((d) => (
              <div key={d.dim} className="flex justify-between gap-3">
                <span style={{ color: "var(--text-secondary)" }}>{d.dim}</span>
                <span className="flex items-center gap-2">
                  <span style={{ color: "var(--text-primary)" }} className="font-medium">{d.atual}</span>
                  {hasPrev && (
                    <span className="text-[10px]" style={{ color: (d.atual - d.anterior) >= 0 ? "var(--success)" : "var(--danger)" }}>
                      {d.atual - d.anterior > 0 ? "+" : ""}{d.atual - d.anterior}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SWOT */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="diagnosis-swot">
        <SwotCard color="var(--success)" title="Forças" icon={TrendUp} items={b.swot?.strengths || []} />
        <SwotCard color="var(--danger)" title="Fraquezas" icon={TrendDown} items={b.swot?.weaknesses || []} />
        <SwotCard color="var(--gold)" title="Oportunidades" icon={Lightning} items={b.swot?.opportunities || []} />
        <SwotCard color="var(--warning)" title="Ameaças" icon={ShieldWarning} items={b.swot?.threats || []} />
      </section>

      {/* Diagnosis blocks */}
      <section className="surface" data-testid="diagnosis-blocks">
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border-default)" }}>
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Análise por dimensão</h2>
        </div>
        <div className="divide-y" style={{ borderColor: "var(--border-default)" }}>
          {(b.diagnosis_blocks || []).map((blk, i) => (
            <div key={i} className="px-6 py-4 border-b last:border-b-0" style={{ borderColor: "var(--border-default)" }}>
              <div className="text-sm font-semibold mb-1" style={{ color: "var(--text-primary)" }}>{blk.title}</div>
              <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{blk.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Next steps */}
      <section className="surface p-6" data-testid="diagnosis-next-steps">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRight size={18} weight="duotone" style={{ color: "var(--gold)" }} />
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Próximos passos sugeridos</h2>
        </div>
        <ul className="space-y-2">
          {(b.next_steps || []).map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              <span className="mt-1.5 brand-dot shrink-0" />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Adaptation request */}
      {b.adaptation_request && (
        <section className="surface p-6" data-testid="diagnosis-adaptation">
          <div className="label-eyebrow mb-2">Adaptação solicitada</div>
          <p className="text-sm italic" style={{ color: "var(--text-primary)" }}>"{b.adaptation_request}"</p>
        </section>
      )}
    </div>
  );
};

const SwotCard = ({ title, icon: Icon, items, color }) => (
  <div className="surface p-5">
    <div className="flex items-center gap-2 mb-3 pb-3 border-b" style={{ borderColor: "var(--border-default)" }}>
      <Icon size={16} weight="duotone" style={{ color }} />
      <span className="label-eyebrow" style={{ color }}>{title}</span>
    </div>
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={i}>
          <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{it.title}</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{it.detail}</div>
        </li>
      ))}
    </ul>
  </div>
);

// ===== History list view =====
const HistoryView = ({ items, onView, onDelete, onBack }) => {
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  return (
    <div className="space-y-6" data-testid="history-view">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">Histórico de Briefings</div>
          <h1 className="h-display" style={{ color: "var(--text-primary)" }}>Evolução estratégica</h1>
          <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
            {items.length} versão(ões) registrada(s). Cada nova submissão é versionada para você acompanhar a maturação ao longo do tempo.
          </p>
        </div>
        <button onClick={onBack} className="btn-ghost px-4 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2" data-testid="history-back">
          <ArrowLeft size={14} /> Voltar
        </button>
      </header>

      <section className="surface">
        <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left" style={{ color: "var(--text-secondary)" }}>
                <th className="px-6 py-3 label-eyebrow">Data</th>
                <th className="px-6 py-3 label-eyebrow">Empresa</th>
                <th className="px-6 py-3 label-eyebrow">Global</th>
                <th className="px-6 py-3 label-eyebrow">Digital</th>
                <th className="px-6 py-3 label-eyebrow">Comercial</th>
                <th className="px-6 py-3 label-eyebrow">Preço</th>
                <th className="px-6 py-3 label-eyebrow">Equipe</th>
                <th className="px-6 py-3 label-eyebrow text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b, idx) => {
                const ms = b.maturity_scores || {};
                const next = items[idx + 1];
                const prevOverall = next?.maturity_scores?.overall;
                const delta = prevOverall != null ? (ms.overall || 0) - prevOverall : null;
                return (
                  <tr key={b.id} className="border-t surface-hover" style={{ borderColor: "var(--border-default)" }} data-testid={`history-row-${b.id}`}>
                    <td className="px-6 py-3 text-xs" style={{ color: "var(--text-secondary)" }}>{fmt(b.created_at)}</td>
                    <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{b.company_name}</td>
                    <td className="px-6 py-3">
                      <span style={{ color: "var(--gold)" }} className="font-medium">{ms.overall ?? "—"}</span>
                      {delta != null && (
                        <span className="ml-2 text-[10px]" style={{ color: delta >= 0 ? "var(--success)" : "var(--danger)" }}>
                          {delta > 0 ? "+" : ""}{delta}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{ms.Digital ?? "—"}</td>
                    <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{ms.Comercial ?? "—"}</td>
                    <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{ms["Precificação"] ?? "—"}</td>
                    <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{ms["Equipe & Processos"] ?? "—"}</td>
                    <td className="px-6 py-3 text-right">
                      <button onClick={() => onView(b)} className="btn-ghost p-1.5 mr-2" data-testid={`view-${b.id}`} title="Ver">
                        <Eye size={14} />
                      </button>
                      <button onClick={() => { if (window.confirm("Remover esta versão?")) onDelete(b.id); }} className="btn-ghost p-1.5" style={{ color: "var(--danger)" }} data-testid={`delete-${b.id}`} title="Remover">
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

// ===== Main page =====
const SECTIONS = [
  { idx: 1, title: "Identificação da operação", icon: Buildings },
  { idx: 2, title: "Situação atual do portfólio", icon: Storefront },
  { idx: 3, title: "Clientes e canais de venda", icon: Users },
  { idx: 4, title: "Concorrência e referência", icon: Target },
  { idx: 5, title: "Precificação, margem e desconto", icon: CurrencyCircleDollar },
  { idx: 6, title: "Presença digital e automação", icon: DeviceMobile },
  { idx: 7, title: "Equipe e contratação", icon: UsersThree },
  { idx: 8, title: "Adaptação do sistema", icon: ChartPieSlice },
];

export default function Diagnostico() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [evolution, setEvolution] = useState(null);
  const [view, setView] = useState("form"); // 'form' | 'diagnosis' | 'history'
  const [openSection, setOpenSection] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const reloadAll = async () => {
    const [latestRes, listRes, evoRes] = await Promise.all([
      api.get("/briefing/latest"),
      api.get("/briefing"),
      api.get("/briefing-evolution"),
    ]);
    setLatest(latestRes.data || null);
    setHistory(listRes.data || []);
    setEvolution(evoRes.data || null);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      try {
        await reloadAll();
        // After initial load, if there's a latest briefing show the diagnosis view
        const r = await api.get("/briefing/latest");
        if (r.data) setView("diagnosis");
      } catch {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.cnpj) {
      toast.error("Preencha pelo menos nome da empresa e CNPJ");
      setOpenSection(1);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/briefing", form);
      await reloadAll();
      setView("diagnosis");
      toast.success("Diagnóstico gerado e versionado no histórico!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar diagnóstico");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewHistoric = (b) => {
    setLatest(b);
    setView("diagnosis");
    window.scrollTo({ top: 0 });
  };

  const handleDeleteHistoric = async (id) => {
    try {
      await api.delete(`/briefing/${id}`);
      await reloadAll();
      toast.success("Briefing removido do histórico");
    } catch {
      toast.error("Erro ao remover");
    }
  };

  if (loading) return <div className="text-sm" style={{ color: "var(--text-secondary)" }}>Carregando…</div>;

  if (view === "history") {
    return (
      <HistoryView
        items={history}
        onView={handleViewHistoric}
        onDelete={handleDeleteHistoric}
        onBack={() => setView(latest ? "diagnosis" : "form")}
      />
    );
  }

  if (view === "diagnosis" && latest) {
    return (
      <>
        <Diagnosis
          b={latest}
          evolution={evolution && latest.id === evolution.current?.id ? evolution : null}
          onReset={() => {
            setForm(EMPTY);
            setOpenSection(1);
            setView("form");
            window.scrollTo({ top: 0 });
          }}
        />
        {history.length > 1 && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setView("history")}
              className="btn-ghost px-4 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2"
              data-testid="view-history"
            >
              <ClockCounterClockwise size={14} /> Ver histórico ({history.length} versões)
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="space-y-6" data-testid="diagnostico-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Diagnóstico Estratégico</div>
          <h1 className="h-display" style={{ color: "var(--text-primary)" }}>Formulário inicial</h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
            Preencha as 8 seções para gerar um diagnóstico automático com matriz SWOT, gráfico de maturidade e próximos passos contextuais. Cada submissão é versionada — assim você acompanha a evolução ao longo do tempo.
          </p>
        </div>
        <div className="flex gap-2">
          {latest && (
            <button onClick={() => setView("diagnosis")} className="btn-ghost px-4 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2" data-testid="view-existing">
              <ArrowLeft size={14} /> Ver diagnóstico atual
            </button>
          )}
          {history.length > 0 && (
            <button onClick={() => setView("history")} className="btn-ghost px-4 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2" data-testid="view-history-from-form">
              <ClockCounterClockwise size={14} /> Histórico ({history.length})
            </button>
          )}
        </div>
      </header>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Section 1 */}
        <Section index={1} title={SECTIONS[0].title} icon={SECTIONS[0].icon} open={openSection === 1} onToggle={() => setOpenSection(openSection === 1 ? 0 : 1)}>
          <Field label="1. Nome da empresa ou marca comercial *">
            <input className="input-base" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} required data-testid="company-name" />
          </Field>
          <Field label="2. CNPJ principal *">
            <input className="input-base" value={form.cnpj} onChange={(e) => set("cnpj", e.target.value)} required data-testid="cnpj" placeholder="00.000.000/0000-00" />
          </Field>
          <Field label="3. Cidade/UF de atuação principal">
            <input className="input-base" value={form.city_uf} onChange={(e) => set("city_uf", e.target.value)} placeholder="Manaus/AM" />
          </Field>
          <Field label="4. A empresa atua principalmente como">
            <Radios value={form.operation_type} onChange={(v) => set("operation_type", v)} options={["Distribuidora", "Atacadista", "Varejista", "Representante comercial", "Indústria / fracionadora", "Revendedora multimarcas", "Outro"]} />
          </Field>
          <Field label="5. Regiões que atende ou pretende atender">
            <Chips value={form.regions} onChange={(v) => set("regions", v)} options={["Manaus", "Interior do Amazonas", "Outros estados da Região Norte", "Todo o Brasil", "Apenas venda local", "Ainda não definido"]} />
          </Field>
        </Section>

        {/* Section 2 */}
        <Section index={2} title={SECTIONS[1].title} icon={SECTIONS[1].icon} open={openSection === 2} onToggle={() => setOpenSection(openSection === 2 ? 0 : 2)}>
          <Field label="6. Categorias que a empresa já comercializa">
            <Chips value={form.current_categories} onChange={(v) => set("current_categories", v)} options={["Temperos secos", "Condimentos", "Molhos", "Alimentos secos", "Grãos e farináceos", "Produtos de mercearia", "Produtos para food service", "Produtos pet / rações", "Utilidades", "Materiais elétricos/hidráulicos", "Outros"]} />
          </Field>
          <Field label="7. Quais produtos têm maior giro atualmente?">
            <textarea className="input-base" rows={2} value={form.top_selling} onChange={(e) => set("top_selling", e.target.value)} />
          </Field>
          <Field label="8. Quais produtos têm melhor margem?">
            <textarea className="input-base" rows={2} value={form.best_margin} onChange={(e) => set("best_margin", e.target.value)} />
          </Field>
          <Field label="9. Quais produtos geram dificuldade de compra/estoque/venda?">
            <textarea className="input-base" rows={2} value={form.pain_products} onChange={(e) => set("pain_products", e.target.value)} />
          </Field>
          <Field label="10. Linhas que deseja ampliar">
            <Chips value={form.expansion_lines} onChange={(v) => set("expansion_lines", v)} options={["Temperos básicos", "Temperos gourmet", "Condimentos", "Molhos", "Produtos regionais", "Food service", "Mercearia seca", "Produtos para revenda em mercadinhos", "Produtos para restaurantes", "Produtos para açougues/churrascarias", "Ainda não definido"]} />
          </Field>
          <Field label="11. Objetivo da pesquisa de produtos">
            <Chips value={form.research_goals} onChange={(v) => set("research_goals", v)} options={["Encontrar produtos de maior giro", "Encontrar produtos com melhor margem", "Concorrer com marcas locais", "Montar linha própria", "Ampliar mix para clientes atuais", "Entrar em novos mercados", "Criar catálogo digital", "Apoiar contratação de profissional de pesquisa", "Outro"]} />
          </Field>
        </Section>

        {/* Section 3 */}
        <Section index={3} title={SECTIONS[2].title} icon={SECTIONS[2].icon} open={openSection === 3} onToggle={() => setOpenSection(openSection === 3 ? 0 : 3)}>
          <Field label="12. Principais clientes atuais">
            <Chips value={form.current_clients} onChange={(v) => set("current_clients", v)} options={["Mercadinhos", "Mercearias", "Supermercados de bairro", "Restaurantes", "Lanchonetes", "Pizzarias", "Açougues", "Churrascarias", "Cozinhas industriais", "Revendedores", "Distribuidores menores", "Consumidor final", "Órgãos públicos", "Outros"]} />
          </Field>
          <Field label="13. Quem deseja atingir com mais força nos próximos meses">
            <Chips value={form.target_clients} onChange={(v) => set("target_clients", v)} options={["Mercadinhos", "Mercearias", "Supermercados de bairro", "Restaurantes", "Lanchonetes", "Pizzarias", "Açougues", "Churrascarias", "Cozinhas industriais", "Revendedores", "Distribuidores menores", "Consumidor final", "Órgãos públicos", "Outros"]} />
          </Field>
          <Field label="14. Como as vendas acontecem hoje">
            <Chips value={form.sales_channels_active} onChange={(v) => set("sales_channels_active", v)} options={["WhatsApp", "Vendedores externos", "Loja física", "Telefone", "Instagram", "Site", "Marketplace", "Indicação", "Representantes", "Outro"]} />
          </Field>
          <Field label="15. A empresa possui base organizada de clientes?">
            <Radios value={form.client_base_organized} onChange={(v) => set("client_base_organized", v)} options={["Sim, em sistema/CRM", "Sim, em planilha", "Sim, mas apenas no WhatsApp", "Parcialmente", "Não"]} />
          </Field>
          <Field label="16. Lista de clientes por segmento?">
            <Radios value={form.has_segment_list} onChange={(v) => set("has_segment_list", v)} options={["Sim", "Não", "Parcialmente"]} />
          </Field>
          <Field label="17. Rotina de recompra/reposição programada?">
            <Radios value={form.has_recurring_routine} onChange={(v) => set("has_recurring_routine", v)} options={["Sim", "Não", "Parcialmente"]} />
          </Field>
          <Field label="18. Ticket médio desejado por pedido B2B">
            <Radios value={form.desired_ticket} onChange={(v) => set("desired_ticket", v)} options={["Até R$ 300", "R$ 300 a R$ 700", "R$ 700 a R$ 1.500", "R$ 1.500 a R$ 3.000", "Acima de R$ 3.000", "Ainda não definido"]} />
          </Field>
        </Section>

        {/* Section 4 */}
        <Section index={4} title={SECTIONS[3].title} icon={SECTIONS[3].icon} open={openSection === 4} onToggle={() => setOpenSection(openSection === 4 ? 0 : 4)}>
          <Field label="19. Concorrentes ou marcas para monitorar">
            <textarea className="input-base" rows={2} value={form.competitors_to_watch} onChange={(e) => set("competitors_to_watch", e.target.value)} placeholder="Dubom, Kitano, Tempero Norte…" />
          </Field>
          <Field label="20. A Dubom deve ser considerada concorrente/referência principal?">
            <Radios value={form.dubom_as_reference} onChange={(v) => set("dubom_as_reference", v)} options={["Sim", "Não", "Parcialmente", "Não sei responder"]} />
          </Field>
          <Field label="21. O que comparar com concorrentes">
            <Chips value={form.compare_aspects} onChange={(v) => set("compare_aspects", v)} options={["Preço", "Embalagem", "Variedade de produtos", "Presença digital", "Canais de venda", "Público atendido", "Promoções", "Mix de produtos", "Condições comerciais", "Outro"]} />
          </Field>
          <Field label="22. Canais a monitorar pelo sistema">
            <Chips value={form.channels_to_monitor} onChange={(v) => set("channels_to_monitor", v)} options={["Site do concorrente", "Mercado Livre", "Amazon", "Shopee", "Magalu", "Bemol", "Atacadão Nova Era", "Supermercados locais", "Outros"]} />
          </Field>
        </Section>

        {/* Section 5 */}
        <Section index={5} title={SECTIONS[4].title} icon={SECTIONS[4].icon} open={openSection === 5} onToggle={() => setOpenSection(openSection === 5 ? 0 : 5)}>
          <Field label="23. Tabela de preços organizada?">
            <Radios value={form.has_price_table} onChange={(v) => set("has_price_table", v)} options={["Sim", "Não", "Parcialmente"]} />
          </Field>
          <Field label="24. Preço diferente por tipo de cliente?">
            <Radios value={form.pricing_strategy} onChange={(v) => set("pricing_strategy", v)} options={["Sim, por volume", "Sim, por segmento", "Sim, por negociação individual", "Não", "Ainda não definido"]} />
          </Field>
          <Field label="25. Margem mínima desejada por produto/categoria?">
            <Radios value={form.has_margin_target} onChange={(v) => set("has_margin_target", v)} options={["Sim", "Não", "Ainda não calculada"]} />
          </Field>
          <Field label="26. Objetivo da comparação de preços">
            <Radios value={form.price_comparison_goal} onChange={(v) => set("price_comparison_goal", v)} options={["Saber se estamos caros ou baratos", "Justificar preço para clientes", "Criar política de desconto", "Melhorar margem", "Encontrar oportunidades de produto", "Criar relatório de economia para clientes", "Outro"]} />
          </Field>
          <Field label="27. Acesso para clientes acompanharem preço/histórico/descontos?">
            <Radios value={form.client_price_access} onChange={(v) => set("client_price_access", v)} options={["Sim", "Não", "Talvez, após validação", "Apenas para clientes selecionados"]} />
          </Field>
        </Section>

        {/* Section 6 */}
        <Section index={6} title={SECTIONS[5].title} icon={SECTIONS[5].icon} open={openSection === 6} onToggle={() => setOpenSection(openSection === 6 ? 0 : 6)}>
          <Field label="28. Site institucional ou catálogo online?">
            <Radios value={form.has_website} onChange={(v) => set("has_website", v)} options={["Sim", "Não", "Em desenvolvimento", "Apenas redes sociais", "Apenas WhatsApp"]} />
          </Field>
          <Field label="29. WhatsApp Business configurado (catálogo, etiquetas, mensagens)?">
            <Radios value={form.whatsapp_business} onChange={(v) => set("whatsapp_business", v)} options={["Sim", "Não", "Parcialmente"]} />
          </Field>
          <Field label="30. Instagram comercial ativo?">
            <Radios value={form.instagram_active} onChange={(v) => set("instagram_active", v)} options={["Sim", "Não", "Parcialmente"]} />
          </Field>
          <Field label="31. Processos que poderiam ser automatizados">
            <Chips value={form.automation_needs} onChange={(v) => set("automation_needs", v)} options={["Pesquisa de preços", "Pesquisa de concorrentes", "Cadastro de produtos", "Catálogo digital", "Orçamentos", "Follow-up de clientes", "Recompra", "Registro de descontos", "Relatórios de mercado", "Controle de vendedores", "Outro"]} />
          </Field>
          <Field label="32. Principal ganho esperado com o sistema">
            <Radios value={form.expected_gain} onChange={(v) => set("expected_gain", v)} options={["Economizar tempo", "Melhorar decisão de portfólio", "Aumentar vendas", "Melhorar margem", "Organizar clientes", "Criar canal digital", "Apoiar contratação de equipe", "Monitorar concorrentes", "Outro"]} />
          </Field>
        </Section>

        {/* Section 7 */}
        <Section index={7} title={SECTIONS[6].title} icon={SECTIONS[6].icon} open={openSection === 7} onToggle={() => setOpenSection(openSection === 7 ? 0 : 7)}>
          <Field label="33. Funções a contratar/estruturar">
            <Chips value={form.roles_to_hire} onChange={(v) => set("roles_to_hire", v)} options={["Pesquisador de produtos", "Comprador", "Vendedor interno", "Vendedor externo", "SDR/prospecção", "Gestor comercial", "Analista de mercado", "Assistente de cadastro", "Ainda não definido"]} />
          </Field>
          <Field label="34. Foco do profissional de pesquisa de produtos">
            <Chips value={form.researcher_focus} onChange={(v) => set("researcher_focus", v)} options={["Novos produtos", "Fornecedores", "Preço de mercado", "Concorrentes", "Tendências", "Margem", "Embalagens", "Produtos regionais", "Produtos para clientes específicos"]} />
          </Field>
          <Field label="35. Foco do time de vendas">
            <Chips value={form.sales_focus} onChange={(v) => set("sales_focus", v)} options={["Prospecção de novos clientes", "Atendimento de clientes atuais", "Reativação de clientes antigos", "Venda por WhatsApp", "Venda externa", "Venda para restaurantes/mercados", "Venda para revendedores", "Outro"]} />
          </Field>
        </Section>

        {/* Section 8 */}
        <Section index={8} title={SECTIONS[7].title} icon={SECTIONS[7].icon} open={openSection === 8} onToggle={() => setOpenSection(openSection === 8 ? 0 : 8)}>
          <Field
            label="Solicitação de adaptação do sistema"
            hint="Ex.: incluir categorias específicas, monitorar concorrentes determinados, adicionar canais locais, criar área B2B, acompanhar histórico de preços, gerar relatórios quinzenais, controlar contatos comerciais, estruturar catálogo digital, apoiar contratação de equipe."
          >
            <textarea
              className="input-base"
              rows={6}
              value={form.adaptation_request}
              onChange={(e) => set("adaptation_request", e.target.value)}
              placeholder="Descreva quais adaptações considera importantes para que a ferramenta reflita melhor a realidade da sua empresa…"
              data-testid="adaptation-request"
            />
          </Field>
        </Section>

        <div className="surface p-5 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 sticky bottom-2 z-30">
          <div className="text-xs flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
            <ClipboardText size={14} weight="duotone" style={{ color: "var(--gold)" }} />
            Ao enviar, geramos automaticamente diagnóstico, matriz SWOT e gráfico de maturidade.
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-gold px-6 py-3 rounded-sm text-xs uppercase tracking-wider flex items-center justify-center gap-2"
            data-testid="briefing-submit"
          >
            {submitting ? "Gerando diagnóstico…" : (<>Gerar diagnóstico <ArrowRight size={14} weight="bold" /></>)}
          </button>
        </div>
      </form>
    </div>
  );
}
